import {
    InputRow,
    SelectRow,
    SwitchRow,
    DateRow,
    DetailRow,
    CreatableSelectRow
} from "../../components/Rows";

import { electricTruckBrandsOptions, electricTruckModelsByBrand, getVehicleTypeOptions } from "../token/VehicleInfo"; 

import { formatSwedishTime } from "../../helpers/DateTime";
import {
    getDecryptedTokenDisplay,
    isTokenAuthorizationNotAccepted,
    isTokenExpired,
    isMaxEnergyKwhInvalid,
    isTokenMarkedInvalid,
} from "../../helpers/TokenValidity";
import { FormCard, FormGrid } from "../../components/FormCard";
import { useAuth, Roles } from "../../providers/AuthContext";
import {
    getCustomerDisplayName,
    shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import { useTranslation } from "react-i18next";

const TokenForm = ({
    token = {},
    handleInputChange,
    customers = [],
    invalidFields = [],
    isEditMode = false,
    onResetAccumulatedEnergy,
}) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const customerOptions = customers.map((customer) => ({
        label: getCustomerDisplayName(customer, shouldShowOrganizationName(hasRole)),
        value: customer.customerPublicId,
    }));
    const selectedCustomer =
        customers.find((customer) => customer.customerPublicId === token.customerPublicId) ||
        token;

    const isAdmin = hasRole(Roles.Admin);

    const baseOptions = ["RFID"];
    const adminOptions = ["AUTOCHARGE", "APP"];

    const typeOptions = (isAdmin ? [...baseOptions, ...adminOptions] : baseOptions)
        .map(type => ({
            label: type,
            value: type,
        }));

    const statusOptions = [
        "Accepted",
        "Blocked",
        "Expired",
        "Invalid",
        "ConcurrentTx",
    ].map((s) => ({ label: t(`token.status.${s}`, { defaultValue: s }), value: s }));


    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const d = new Date(dateString);
        return d.toISOString().slice(0, 16);
    };
    const vehicleInfo = token.tokenVehicleInfo || {};
    const isInternalToken = token?.isFromOcpi !== true;

    const handleVehicleChange = (key, value) => {
        if (key === "vehicleBrand" && isAdmin) {
            handleInputChange("tokenVehicleInfo", {
                ...token.tokenVehicleInfo,
                vehicleBrand: value,
                vehicleModel: null,
            });
            return;
        }

        handleInputChange("tokenVehicleInfo", {
            ...token.tokenVehicleInfo,
            [key]: value,
        });
    };


    return (
        <>
            <FormCard title={t("form.tokenDetails")} className="shadow-sm">
                <FormGrid>
                    <InputRow
                        label={t("common.name")}
                        value={token?.name || token?.visualNumber || ""}
                        invalid={invalidFields.includes("name")}
                        onChange={(v) => handleInputChange("name", v)}
                    />

                    {!isEditMode ? (
                        <SelectRow
                            label={t("entity.customer")}
                            value={token?.customerPublicId}
                            options={customerOptions}
                            required
                            invalid={invalidFields.includes("customerPublicId")}
                            onChange={(v) => handleInputChange("customerPublicId", v)}
                        />
                    ) : (
                        <DetailRow
                            label={t("entity.customer")}
                            value={getCustomerDisplayName(
                                selectedCustomer,
                                shouldShowOrganizationName(hasRole)
                            )}
                        />
                    )}


                        <SelectRow
                            label={t("common.type")}
                            value={token?.tokenType}
                            options={typeOptions}
                            required
                            invalid={invalidFields.includes("tokenType")}
                            onChange={(v) => handleInputChange("tokenType", v)}
                        />

                    <SelectRow
                        label={t("form.authorizationStatus")}
                        value={token?.authorizationStatus}
                        options={statusOptions}
                        required
                        invalid={
                            invalidFields.includes("authorizationStatus")
                            || isTokenAuthorizationNotAccepted(token)
                        }
                        invalidMessage={
                            isTokenAuthorizationNotAccepted(token)
                                ? t("token.notAccepted")
                                : undefined
                        }
                        onChange={(v) => handleInputChange("authorizationStatus", v)}
                    />

                    {!isEditMode ? (
                        <InputRow
                            label={t("form.tokenValue")}
                            value={token?.tagToken || ""}
                            invalid={invalidFields.includes("tagToken")}
                            onChange={(v) => handleInputChange("tagToken", v)}
                        />
                    ) : (
                        <div className="md:col-span-2">
                            <FormGrid>
                                <DetailRow
                                    label={t("form.tokenValue")}
                                    value={token.tagToken}
                                />
                                <DetailRow
                                    label={t("token.decrypted")}
                                    value={getDecryptedTokenDisplay(token, t)}
                                />
                            </FormGrid>
                        </div>
                    )}

                    <SwitchRow
                        label={t("common.valid")}
                        checked={!!token?.isValid}
                        invalid={isTokenMarkedInvalid(token)}
                        invalidMessage={t("token.markedInvalid")}
                        onChange={(v) => handleInputChange("isValid", Boolean(v))}
                    />

                    <DateRow
                        label={t("form.validUntil")}
                        value={formatDateForInput(token?.expiredDateTimeUtc)}
                        invalid={isTokenExpired(token)}
                        invalidMessage={t("token.validUntilPassed")}
                        onChange={(v) => handleInputChange("expiredDateTimeUtc", v)}
                    />

                    {isInternalToken && (
                        <InputRow
                            label={t("token.maxEnergyKwh")}
                            value={token?.maxEnergyKwh ?? ""}
                            isNumber
                            invalid={isMaxEnergyKwhInvalid(token?.maxEnergyKwh)}
                            onChange={(v) => handleInputChange("maxEnergyKwh", v === "" ? null : v)}
                            action={
                                token?.maxEnergyKwh != null ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => handleInputChange("maxEnergyKwh", null)}
                                    >
                                        {t("token.removeMaxEnergy")}
                                    </button>
                                ) : null
                            }
                        />
                    )}

                    {isEditMode && isInternalToken && (
                        <InputRow
                            label={t("token.accumulatedEnergyKwh")}
                            value={token?.accumulatedEnergyKwh ?? 0}
                            disabled
                            action={
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={onResetAccumulatedEnergy}
                                >
                                    {t("token.resetAccumulatedEnergy")}
                                </button>
                            }
                        />
                    )}

                    {isEditMode && (
                        <DetailRow
                            label={t("form.lastUpdated")}
                            value={formatSwedishTime(token.updatedDateTimeUtc)}
                        />
                    )}
                </FormGrid>
            </FormCard>

            {isInternalToken && (
                <FormCard title={t("form.vehicle")} className="shadow-sm">
                    <FormGrid>
                        <SelectRow
                            label={t("common.type")}
                            value={vehicleInfo.vehicleType || ""}
                            options={getVehicleTypeOptions(t)}
                            isClearable
                            onChange={(v) => handleVehicleChange("vehicleType", v)}
                        />

                        {isAdmin ? (
                            <CreatableSelectRow
                                label={t("form.vehicleBrand")}
                                value={vehicleInfo.vehicleBrand || null}
                                options={electricTruckBrandsOptions}
                                placeholder={t("token.selectBrand")}
                                onChange={(v) => handleVehicleChange("vehicleBrand", v)}
                            />
                        ) : (
                            <InputRow
                                label={t("form.vehicleBrand")}
                                value={vehicleInfo.vehicleBrand || ""}
                                onChange={(v) => handleVehicleChange("vehicleBrand", v)}
                            />
                        )}

                        {isAdmin ? (
                            <CreatableSelectRow
                                label={t("form.vehicleModel")}
                                value={vehicleInfo.vehicleModel || null}
                                options={electricTruckModelsByBrand[vehicleInfo.vehicleBrand] || []}
                                placeholder={t("token.selectModel")}
                                onChange={(v) => handleVehicleChange("vehicleModel", v)}
                            />
                        ) : (
                            <InputRow
                                label={t("form.vehicleModel")}
                                value={vehicleInfo.vehicleModel || ""}
                                onChange={(v) => handleVehicleChange("vehicleModel", v)}
                            />
                        )}

                        <InputRow
                            label={t("form.registrationNumber")}
                            value={vehicleInfo.registrationNumber || ""}
                            onChange={(v) => handleVehicleChange("registrationNumber", v)}
                        />

                        <InputRow
                            label={t("form.driverName")}
                            value={vehicleInfo.driverName || ""}
                            onChange={(v) => handleVehicleChange("driverName", v)}
                        />

                        <InputRow
                            label={t("form.driverPhone")}
                            value={vehicleInfo.driverPhoneNumber || ""}
                            onChange={(v) => handleVehicleChange("driverPhoneNumber", v)}
                        />
                    </FormGrid>
                </FormCard>
            )}
        </>
    );
};

export default TokenForm;
